import * as types from 'constants/action_types'
import { v4 as uuidV4 } from 'uuid'
import { normalizeAttachment } from 'util/file'
import { sizeInBytes } from 'util/strings'

import {
  UPDATE_DRAFT,
  CREATE_DRAFT,
  STORED_DRAFT,
  DELETE_DRAFT,
  FAILED_SEND,
} from 'ducks/drafts2/constants'
import {
  isDraftBodySizeWithinLimit,
  DRAFT_BODY_SIZE_EXCEED_MAX_LIMIT_MSG,
} from 'util/draft'
import { omit } from 'util/objects'

const isSendableTwitter = draft => {
  const bodyIsEmpty = !draft.body || draft.body === ''
  if (
    draft.twitterRecipients &&
    draft.twitterRecipients.filter(r => r.twitter && r.twitter.username)
      .length > 0 &&
    !bodyIsEmpty
  ) {
    return { isSendable: true }
  }
  return { isSendable: false }
}

const isSendableAndReason = draft => {
  if (draft.isTwitter) return isSendableTwitter(draft)

  const bodyIsEmpty = !draft.body || draft.body === ''
  const isNote = draft.isNote
  if (!isNote && !draft.to) return { isSendable: false }
  if (!isNote && draft.to.length === 0) return { isSendable: false }

  const hasAttachments =
    draft.attachmentsNormalized && draft.attachmentsNormalized.length > 0
  if (bodyIsEmpty && !draft.isForwarding && !hasAttachments)
    return { isSendable: false }

  if (!isDraftBodySizeWithinLimit(draft.bodySizeBytes)) {
    return {
      isSendable: false,
      notSendableReason: DRAFT_BODY_SIZE_EXCEED_MAX_LIMIT_MSG,
    }
  }

  return { isSendable: true }
}

const defaultState = {
  byTicketId: {},
  byMessageId: {},
  byId: {},
}

const reducers = {}

const updateDraftInStore = (
  state,
  draftType,
  id,
  fields,
  ticketId,
  messageId
) => {
  const { byId } = state
  const draft = byId[id]
  const currentVersion = (draft && draft.version) || 1
  const version = currentVersion + 1
  const newDraft = { ...draft, id, ...fields, ticketId, messageId, version }
  newDraft.bodySizeBytes = sizeInBytes(newDraft.body)

  const { isSendable, notSendableReason } = isSendableAndReason(newDraft)
  newDraft.isSendable = isSendable
  newDraft.notSendableReason = notSendableReason

  const currentTicketStore = state.byTicketId[ticketId] || {}
  return {
    ...state,
    byTicketId: {
      ...state.byTicketId,
      [ticketId]: {
        ...currentTicketStore,
        [draftType]: id,
      },
    },
    byId: {
      ...state.byId,
      [id]: newDraft,
    },
  }
}

const putDraftInStore = (
  state,
  draftType,
  id,
  version,
  fields,
  ticketId,
  messageId,
  serverVersion
) => {
  const newDraft = {
    id,
    ...fields,
    ticketId,
    messageId,
    version,
    serverVersion,
  }
  newDraft.bodySizeBytes = sizeInBytes(newDraft.body)

  const { isSendable, notSendableReason } = isSendableAndReason(newDraft)
  newDraft.isSendable = isSendable
  newDraft.notSendableReason = notSendableReason

  const currentTicketStore = state.byTicketId[ticketId] || {}
  return {
    ...state,
    byTicketId: {
      ...state.byTicketId,
      [ticketId]: {
        ...currentTicketStore,
        [draftType]: id,
      },
    },
    byId: {
      ...state.byId,
      [id]: newDraft,
    },
  }
}

const deleteDraftInStore = (state, ticketId, draftId) => {
  const { byId, byTicketId } = state
  const newById = { ...byId }
  const newByTicketId = { ...byTicketId }
  const draft = byId[draftId]
  const draftType = draft.draftType
  const ticketStore = newByTicketId[ticketId] || {}
  delete newById[draftId]

  return {
    ...state,
    byTicketId: {
      ...newByTicketId,
      [ticketId]: omit([draftType], ticketStore),
    },
    byId: newById,
  }
}

reducers[CREATE_DRAFT] = (state, action) => {
  const { draftId, draftType, fields, ticketId, messageId } = action.data
  const id = draftId || uuidV4()

  return putDraftInStore(state, draftType, id, 1, fields, ticketId, messageId)
}

reducers[FAILED_SEND] = (state, action) => {
  const { draft } = action.data
  return putDraftInStore(
    state,
    draft.draftType,
    draft.id,
    draft.version,
    draft,
    draft.ticketId,
    draft.messageId,
    draft.serverVersion
  )
}

reducers[UPDATE_DRAFT] = (state, action) => {
  const { draftId: id, draftType, fields, ticketId, messageId } = action.data

  return updateDraftInStore(state, draftType, id, fields, ticketId, messageId)
}

reducers[DELETE_DRAFT] = (state, action) => {
  const { draftId, ticketId } = action.data

  return deleteDraftInStore(state, ticketId, draftId)
}

reducers[STORED_DRAFT] = (state, action) => {
  const { draftId, serverVersion } = action.data
  const { byId } = state
  const draft = byId[draftId]

  return {
    ...state,
    byId: {
      ...state.byId,
      [draftId]: { ...draft, serverVersion },
    },
  }
}

reducers[types.FETCH_TICKET_SUCCESS] = (state, action) => {
  const data = action.data
  const { ticket } = data
  let newState = state

  const messageId = null
  const conversationDrafts = ticket.conversationDrafts
  if (conversationDrafts && conversationDrafts.length > 0) {
    const ticketId = ticket.id
    conversationDrafts.forEach(draft => {
      try {
        const parsed = JSON.parse(draft.payload)
        const version = draft.version
        parsed.version = version
        newState = putDraftInStore(
          newState,
          draft.draftType,
          draft.id,
          version,
          parsed,
          ticketId,
          messageId,
          version
        )
      } catch (e) {
        // pass
      }
    })
  }

  return newState
}

// eslint-disable-next-line no-multi-assign
reducers[types.SEARCH_SUCCESS] = reducers[types.MERGEABLE_SEARCH_SUCCESS] = (
  state,
  action
) => {
  const data = action.data
  const { tickets } = data
  let newState = state

  tickets.forEach(ticket => {
    const messageId = null
    const conversationDrafts = ticket.conversationDrafts
    if (conversationDrafts && conversationDrafts.length > 0) {
      const ticketId = ticket.id
      conversationDrafts.forEach(draft => {
        try {
          const parsed = JSON.parse(draft.payload)
          const version = draft.version
          parsed.version = version
          newState = putDraftInStore(
            newState,
            draft.draftType,
            draft.id,
            version,
            parsed,
            ticketId,
            messageId,
            version
          )
        } catch (e) {
          // pass
        }
      })
    }
  })

  return newState
}

reducers[types.ATTACHMENT_UPLOAD_FINISHED] = (state, action) => {
  const { ticketId, draftId, file, url, key, expiringUrl } = action.data

  if (draftId === undefined) return state
  if (draftId === null) return state

  const messageId = null
  const { byId } = state
  const draft = byId[draftId]

  if (!draft) return state

  const attachments = draft.attachments || {}

  attachments[file.name] = {
    url,
    expiringUrl,
    filename: file.name,
    file_size: file.size,
    content_type: file.type,
    key,
  }

  const fields = {
    attachments,
    attachmentsNormalized: Object.values(attachments).map(normalizeAttachment),
  }

  return updateDraftInStore(
    state,
    draft.draftType,
    draftId,
    fields,
    ticketId,
    messageId
  )
}

reducers[types.DELETE_ATTACHMENT] = (state, action) => {
  const { ticketId, draftId, file } = action.data

  const messageId = null
  if (draftId === undefined) return state
  if (draftId === null) return state

  const { byId } = state
  const draft = byId[draftId]

  if (!draft) return state

  const attachments = draft.attachments || {}

  delete attachments[file.file_name]

  const fields = {
    attachments,
    attachmentsNormalized: Object.values(attachments).map(normalizeAttachment),
  }

  return updateDraftInStore(
    state,
    draft.draftType,
    draftId,
    fields,
    ticketId,
    messageId
  )
}

export default function reducer(state = defaultState, action) {
  // this is here because a long reducer with many ifs is unreadable
  const handler = reducers[action.type]
  if (handler) return handler(state, action)
  return state
}
